import React, { useState } from 'react';
import { X, Lock, Key, ShieldCheck, Mail, ArrowLeft, Loader2 } from 'lucide-react';
import { auth, GoogleAuthProvider, signInWithPopup } from '../src/firebase';
import { sendPasswordResetEmail } from 'firebase/auth';

interface AdminLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: (status: boolean) => void;
}

export const AdminLoginModal: React.FC<AdminLoginModalProps> = ({ isOpen, onClose, onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Reset Password State
  const [isResetMode, setIsResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');

  if (!isOpen) return null;

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      if (result.user.email === 'vishal.aklavya@gmail.com' || result.user.email === 'tista.gaurav@gmail.com') {
        onLogin(true);
        onClose();
      } else {
        setError('You do not have admin privileges.');
      }
    } catch (error: any) {
      setError(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    // Hardcoded credentials for demonstration
    if (username === 'admin' && password === 'Vk@14111994') {
      onLogin(true);
      onClose();
      setUsername('');
      setPassword('');
      setError('');
    } else {
      setError('Invalid credentials');
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) {
        setError('Please enter your email.');
        return;
    }
    
    setLoading(true);
    setError('');
    try {
        await sendPasswordResetEmail(auth, resetEmail);
        setResetMessage(`Password reset link has been sent to ${resetEmail}`);
        
        // Auto return to login after delay
        setTimeout(() => {
            setIsResetMode(false);
            setResetMessage('');
            setResetEmail('');
        }, 3000);
    } catch (error: any) {
        setError(error.message || 'Failed to send reset email.');
    } finally {
        setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsResetMode(!isResetMode);
    setError('');
    setResetMessage('');
    setUsername('');
    setPassword('');
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-md animate-fadeIn">
      <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all relative">
        {/* Close Button */}
        <button 
          onClick={onClose} 
          className="absolute top-4 right-4 sm:top-5 sm:right-5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 p-2 rounded-full transition-colors z-10"
        >
          <X size={18} className="sm:w-5 sm:h-5" />
        </button>

        <div className="px-6 py-8 sm:px-8 sm:pt-10 sm:pb-8">
          {/* Header Section */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="mx-auto bg-orange-100 w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-4 shadow-inner">
              <ShieldCheck size={24} className="text-orange-600 sm:w-8 sm:h-8" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
              {isResetMode ? 'Reset Password' : 'Admin Portal'}
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 mt-1.5 sm:mt-2">
              {isResetMode 
                ? 'Enter your email to receive reset instructions' 
                : 'Sign in to access the administrative dashboard'}
            </p>
          </div>
          
          {isResetMode ? (
            <form onSubmit={handleReset} className="space-y-5">
               {resetMessage ? (
                 <div className="p-4 bg-emerald-50 border border-emerald-100 text-emerald-700 text-sm rounded-xl flex items-start">
                    <span className="mr-2 mt-0.5">✓</span> 
                    <span>{resetMessage}</span>
                 </div>
               ) : (
                 <>
                   {error && (
                      <div className="p-4 bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl flex items-start animate-pulse">
                        <span className="mr-2 mt-0.5">⚠️</span> 
                        <span>{error}</span>
                      </div>
                    )}
                   <div>
                     <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address</label>
                     <div className="relative">
                       <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                         <Mail size={18} className="text-gray-400" />
                       </div>
                       <input
                         type="email"
                         required
                         value={resetEmail}
                         onChange={(e) => setResetEmail(e.target.value)}
                         className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:bg-white focus:ring-4 focus:ring-orange-500/15 focus:border-orange-500 outline-none transition-all"
                         placeholder="admin@example.com"
                       />
                     </div>
                   </div>
                   <button 
                     type="submit" 
                     className="w-full bg-orange-600 text-white font-bold py-3 rounded-xl hover:bg-orange-700 transition-colors shadow-md shadow-orange-600/20 active:scale-[0.98]"
                   >
                     Send Reset Link
                   </button>
                 </>
               )}
               <button 
                  type="button" 
                  onClick={toggleMode}
                  className="w-full mt-2 text-gray-500 text-sm hover:text-gray-900 flex items-center justify-center font-medium transition-colors"
              >
                  <ArrowLeft size={16} className="mr-1.5" /> Back to Login
              </button>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="space-y-5">
              {error && (
                <div className="p-4 bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl flex items-start animate-pulse">
                  <span className="mr-2 mt-0.5">⚠️</span> 
                  <span>{error}</span>
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Username</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <Lock size={18} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:bg-white focus:ring-4 focus:ring-orange-500/15 focus:border-orange-500 outline-none transition-all"
                      placeholder="Enter username"
                    />
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-1.5">
                     <label className="block text-sm font-semibold text-gray-700">Password</label>
                     <button 
                       type="button"
                       onClick={toggleMode} 
                       className="text-sm text-orange-600 hover:text-orange-700 font-semibold hover:underline transition-colors"
                     >
                       Forgot Password?
                     </button>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <Key size={18} className="text-gray-400" />
                    </div>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:bg-white focus:ring-4 focus:ring-orange-500/15 focus:border-orange-500 outline-none transition-all"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </div>

              <button 
                type="submit" 
                className="w-full bg-gray-900 text-white font-bold py-3 rounded-xl hover:bg-black transition-all shadow-md active:scale-[0.98] mt-2"
              >
                Sign In
              </button>

              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500 font-medium">Or continue with</span>
                </div>
              </div>

              <button 
                type="button"
                onClick={handleGoogleLogin}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 bg-white border border-gray-200 py-3 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm font-semibold text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
              >
                {loading ? (
                  <Loader2 size={20} className="animate-spin text-gray-400" />
                ) : (
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" />
                )}
                {loading ? 'Authenticating...' : 'Sign in with Google'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
